import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Clock,
  Facebook,
  Instagram,
  Mail,
  MapPin,
  Phone,
  Twitter,
  Youtube,
} from "lucide-react";
import { NAV, SITE, SOCIAL } from "../data/site";

const SOCIAL_ICON = {
  youtube: Youtube,
  facebook: Facebook,
  instagram: Instagram,
  x: Twitter,
};
import { LotusRule, TempleGateLogo } from "./Ornaments";

// Footer link columns come from the same menu data as the header, so both stay in step.
const ABOUT_LINKS = NAV.find(g => g.label === "About")?.children ?? [];
const EXPLORE_LINKS = [
  { label: "Guruvani", href: "/guruvani" },
  ...(NAV.find(g => g.label === "Library")?.children ?? []),
  ...(NAV.find(g => g.label === "Gallery")?.children ?? []),
  ...(NAV.find(g => g.label === "Community")?.children ?? []),
];

export function Footer() {
  const tel = `tel:${SITE.phone.replace(/[^+\d]/g, "")}`;
  return (
    <footer className="site-footer">
      <div className="footer-grid container">
        <div className="footer-about">
          <Link
            to="/"
            className="brand brand--heritage brand--footer"
            aria-label={`${SITE.name} – home`}
          >
            <TempleGateLogo className="brand-gate" />
            <span className="brand-text">
              <span className="brand-name">{SITE.name}</span>
              <LotusRule className="brand-rule" />
            </span>
          </Link>
          <p>
            Preserving and sharing the timeless wisdom of Jain philosophy
            through Gurudev Muni Jambuvijayaji Maharaj Saheb's teachings,
            research tools, and scholarly resources.
          </p>
          <ul className="footer-social" aria-label="Follow us">
            {SOCIAL.map(({ network, label, href }) => {
              const Icon = SOCIAL_ICON[network];
              return (
                <li key={network}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${label} (opens in a new tab)`}
                    title={label}
                  >
                    <Icon aria-hidden="true" />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        <nav className="footer-links" aria-labelledby="footer-about-h">
          <h2 className="footer-heading" id="footer-about-h">
            About
          </h2>
          <ul>
            {ABOUT_LINKS.map(l => (
              <li key={l.href}>
                <Link to={l.href}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav className="footer-links" aria-labelledby="footer-explore-h">
          <h2 className="footer-heading" id="footer-explore-h">
            Explore
          </h2>
          <ul>
            {EXPLORE_LINKS.map(l => (
              <li key={l.href}>
                <Link to={l.href}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="footer-contact">
          <h2 className="footer-heading">Visit & Contact</h2>
          <ul>
            <li>
              <MapPin aria-hidden="true" />
              <span>{SITE.address.join(" ")}</span>
            </li>
            <li>
              <Mail aria-hidden="true" />
              <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
            </li>
            <li>
              <Phone aria-hidden="true" />
              <a href={tel}>{SITE.phone}</a>
            </li>
            <li>
              <Clock aria-hidden="true" />
              <span>
                <span className="block">Office: {SITE.officeHours}</span>
                <span className="block">Visiting: {SITE.visitingHours}</span>
              </span>
            </li>
          </ul>
          <a
            href={SITE.mapUrl}
            target="_blank"
            rel="noreferrer"
            className="footer-map"
          >
            Get directions
            <ArrowUpRight aria-hidden="true" />
          </a>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-inner container">
          <p>
            © {new Date().getFullYear()} {SITE.name}. Supported by the{" "}
            <a href={SITE.adaniUrl} target="_blank" rel="noreferrer">
              Adani Foundation
            </a>
            .
          </p>
          <div className="footer-legal">
            <a href={SITE.parivarUrl} target="_blank" rel="noreferrer">
              Siddhi Jambu Parivar
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
